import React from 'react'
import { Box, Paper, Typography, Skeleton, Tooltip } from '@mui/material'
import {
  aggregationLabel,
  formatMetricValue,
  type AggregationType,
  type EntityMetricValue,
  type FormatType,
} from './metrics-config'
import { TRANSITIONS, BRAND_PURPLE } from './design-tokens'

const VIDEO_METRIC_COLOR = BRAND_PURPLE

// Entity metric keys to display, in display order (catalog keys, part 2 §3 —
// NOT the legacy pipeline label keys). Hidden: reach, views, comments, shares,
// likes, total engagements, engagement score, engagement rate.
//
// This is also the DEFAULT `keys` prop, so a caller that does not opt into the
// grid behaviour below sees exactly the KPI strip's historical two tiles.
const VIDEO_METRIC_KEYS = [
  'meaningful_er_pct',
  'u35_pct',
]

/**
 * A tile's worth of data. Unlike `KeyedEntityMetricValue` (`metrics-config.ts`)
 * `value` may be `null` — a requested key with no data, rendered under
 * `renderEmpty` (see below) rather than dropped.
 */
interface DisplayMetric {
  key: string
  value: number | null
  agg: AggregationType
  entityCount: number
  displayName: string
  format: FormatType
}

/**
 * Title-cases a metric key for display when no catalog metadata is available
 * for it (the key is absent from `metrics` entirely — see `renderEmpty`).
 * Mirrors `LabelFilterBuilder.tsx`'s `formatLabelKey`.
 */
function titleCaseMetricKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

export interface MetricsCardsProps {
  /**
   * Per-metric values from `POST /entities/metrics/query` (the single
   * groupBy:none group), keyed by catalog metric key. `null` while unloaded.
   */
  metrics: Record<string, EntityMetricValue> | null
  /** Whether data is still loading */
  isLoading: boolean
  /** Total number of videos (entities) matching the filters */
  videoCount?: number
  /**
   * Metric keys to render, in order. Defaults to the KPI strip's own two
   * tiles (`meaningful_er_pct`, `u35_pct`) — a caller that wants a different
   * set (e.g. a customer grid cell) names it explicitly.
   */
  keys?: string[]
  /**
   * When true, a requested key with no data — absent from `metrics` entirely,
   * or present with `entityCount === 0` — still draws a tile showing "—"
   * instead of vanishing from the strip. Off by default, matching the KPI
   * strip's historical behaviour (a metric no entity carries is absent).
   */
  renderEmpty?: boolean
}

/**
 * Renders a single metric card with white background and colored border/text.
 *
 * Display metadata (name, format, aggregation) travels with the value from the
 * server catalog — the client no longer has a metric config of its own.
 */
const MetricCardItem: React.FC<{ metric: DisplayMetric; color: string }> = ({ metric, color }) => {
  const { value, entityCount, displayName, format, agg } = metric
  const displayValue = formatMetricValue(value, format)
  const aggLabel = aggregationLabel(agg)

  return (
    <Tooltip
      title={`${aggLabel} of ${entityCount} post${entityCount !== 1 ? 's' : ''}`}
      placement="top"
      arrow
    >
      <Paper
        elevation={0}
        // The tile and its value are addressed by the CATALOG METRIC KEY, not
        // by `displayName` — the display name travels with the value from the
        // server catalog and is free to change without a client release, so a
        // test pinned to it would break on a copy edit alone.
        data-testid={`autocoder-social-metric-${metric.key}`}
        sx={{
          flex: 1,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          bgcolor: 'white',
          borderRight: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'rgba(0,0,0,0.06)',
          textAlign: 'center',
          overflow: 'hidden',
          transition: TRANSITIONS.card,
          cursor: 'default',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: 'grey.500',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          {displayName}
        </Typography>

        <Typography
          variant="h3"
          component="div"
          data-testid={`autocoder-social-metric-value-${metric.key}`}
          sx={{
            fontWeight: 700,
            color: color,
            my: 1,
          }}
        >
          {displayValue}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'grey.500',
          }}
        >
          {aggLabel} from {entityCount} post{entityCount !== 1 ? 's' : ''}
        </Typography>
      </Paper>
    </Tooltip>
  )
}

/**
 * Renders a loading skeleton for a single metric card.
 */
const MetricCardSkeleton: React.FC = () => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      p: 4,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      borderRadius: 0,
      bgcolor: 'white',
      borderRight: '1px solid',
      borderBottom: '1px solid',
      borderColor: 'rgba(0,0,0,0.06)',
      opacity: 0.6,
    }}
  >
    <Skeleton variant="text" width={60} height={12} />
    <Skeleton variant="text" width={80} height={36} sx={{ mt: 1 }} />
    <Skeleton variant="text" width={70} height={12} sx={{ mt: 0.5 }} />
  </Paper>
)

/**
 * Renders a row of metric cards
 */
const MetricRow: React.FC<{
  metrics: DisplayMetric[]
  color: string
  isLoading: boolean
  targetCount: number
}> = ({ metrics, color, isLoading, targetCount }) => {
  if (isLoading) {
    return (
      <>
        {[...Array(targetCount)].map((_, index) => (
          <MetricCardSkeleton key={index} />
        ))}
      </>
    )
  }

  if (metrics.length === 0) {
    return null
  }

  return (
    <>
      {metrics.map((metric) => (
        <MetricCardItem key={metric.key} metric={metric} color={color} />
      ))}
    </>
  )
}

/**
 * MetricsCards Component
 *
 * Displays the entity-metric tiles (Meaningful ER, U35) of the KPI strip,
 * straight from the `/entities/metrics/query` response — no client-side
 * aggregation.
 */
export const MetricsCards: React.FC<MetricsCardsProps> = ({
  metrics,
  isLoading,
  keys = VIDEO_METRIC_KEYS,
  renderEmpty = false,
}) => {
  const visibleMetrics: DisplayMetric[] = keys
    .map((key): DisplayMetric | undefined => {
      const metric = metrics?.[key]
      if (metric && metric.entityCount > 0) return { key, ...metric }
      // A metric no entity carries is absent from the tile strip, matching the
      // old getValidMetrics(value !== null) behaviour — unless the caller asked
      // for the gap to show, in which case it draws a "—" placeholder tile
      // rather than vanish (a grid cell must keep its shape).
      if (!renderEmpty) return undefined
      return {
        key,
        value: null,
        agg: metric?.agg ?? 'sum',
        entityCount: metric?.entityCount ?? 0,
        displayName: metric?.displayName ?? titleCaseMetricKey(key),
        format: metric?.format ?? 'number',
      }
    })
    .filter((m): m is DisplayMetric => m !== undefined)

  if (isLoading) {
    return (
      <MetricRow
        metrics={[]}
        color={VIDEO_METRIC_COLOR}
        isLoading={true}
        targetCount={keys.length}
      />
    )
  }

  if (visibleMetrics.length === 0) {
    return null
  }

  return (
    <MetricRow
      metrics={visibleMetrics}
      color={VIDEO_METRIC_COLOR}
      isLoading={false}
      targetCount={keys.length}
    />
  )
}

export default MetricsCards
